import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Checkbox } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { TeamListItem } from "app/pages/sales-intelligence/modules/teams/types/teams.types";

import { WizardStepHeading } from "../WizardStepHeading";
import {
    StyledTeamScopeOption,
    StyledTeamScopeRadioButton,
    StyledTeamSelectionCard,
    StyledTeamSelectionGrid,
    StyledTeamStepScroll,
} from "../styles";
import { ScoringRuleTeamScope } from "../teamScope";

type TeamScopeOption = {
    scope: ScoringRuleTeamScope;
    titleKey: string;
    descriptionKey: string;
    automation: string;
};

const TEAM_SCOPE_OPTIONS: readonly TeamScopeOption[] = [
    {
        scope: ScoringRuleTeamScope.Account,
        titleKey: "si.pages.score_settings.wizard.step3.scope.account.title",
        descriptionKey: "si.pages.score_settings.wizard.step3.scope.account.description",
        automation: "scoring-rule-wizard.team-scope-account",
    },
    {
        scope: ScoringRuleTeamScope.AllTeams,
        titleKey: "si.pages.score_settings.wizard.step3.scope.all_teams.title",
        descriptionKey: "si.pages.score_settings.wizard.step3.scope.all_teams.description",
        automation: "scoring-rule-wizard.team-scope-all-teams",
    },
    {
        scope: ScoringRuleTeamScope.SpecificTeams,
        titleKey: "si.pages.score_settings.wizard.step3.scope.specific_teams.title",
        descriptionKey: "si.pages.score_settings.wizard.step3.scope.specific_teams.description",
        automation: "scoring-rule-wizard.team-scope-specific-teams",
    },
];

type Props = {
    teams: readonly TeamListItem[];
    scope: ScoringRuleTeamScope;
    selectedTeamIds: readonly number[];
    onSelectScope: (scope: ScoringRuleTeamScope) => void;
    onToggleTeam: (teamId: number) => void;
};

export const Step3TeamSelection = ({ teams, scope, selectedTeamIds, onSelectScope, onToggleTeam }: Props) => {
    const { translate } = useTranslate();

    const selectedSet = useMemo(() => new Set(selectedTeamIds), [selectedTeamIds]);
    const showTeams = scope !== ScoringRuleTeamScope.Account;

    return (
        <Stack
            gap="24px"
            width="100%"
            flex="1"
            minHeight="0"
            data-automation="scoring-rule-wizard.step-team"
        >
            <WizardStepHeading
                headingKey="si.pages.score_settings.wizard.step3.heading"
                descriptionKey="si.pages.score_settings.wizard.step3.description"
            />

            <StyledTeamStepScroll>
                <Stack gap="24px">
                    <Stack
                        gap="12px"
                        data-automation="scoring-rule-wizard.team-scope-options"
                    >
                        {TEAM_SCOPE_OPTIONS.map((option) => (
                            <StyledTeamScopeOption
                                key={option.scope}
                                $isSelected={scope === option.scope}
                                onClick={() => onSelectScope(option.scope)}
                                data-automation={option.automation}
                            >
                                <StyledTeamScopeRadioButton
                                    itemSelected={scope === option.scope}
                                    onClick={() => onSelectScope(option.scope)}
                                    renderLabel={() => (
                                        <Stack
                                            gap="4px"
                                            margin="0 0 0 12px"
                                        >
                                            <Text
                                                fontVariant="subtitle2"
                                                color={colorsPalettes.carbon["500"]}
                                            >
                                                {translate(option.titleKey)}
                                            </Text>
                                            <Text
                                                fontVariant="caption"
                                                color={colorsPalettes.carbon["300"]}
                                            >
                                                {translate(option.descriptionKey)}
                                            </Text>
                                        </Stack>
                                    )}
                                />
                            </StyledTeamScopeOption>
                        ))}
                    </Stack>

                    {showTeams ? (
                        <Stack gap="12px">
                            <Flex
                                alignItems="center"
                                justifyContent="space-between"
                                gap="8px"
                            >
                                <Text
                                    fontVariant="subtitle2"
                                    color={colorsPalettes.carbon["500"]}
                                >
                                    {translate("si.pages.score_settings.wizard.step3.select_teams_label")}
                                </Text>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon["300"]}
                                >
                                    {translate("si.pages.score_settings.wizard.step3.select_teams_hint")}
                                </Text>
                            </Flex>

                            <StyledTeamSelectionGrid data-automation="scoring-rule-wizard.teams-grid">
                                {teams.map((team) => {
                                    const isSelected = selectedSet.has(team.teamId);

                                    return (
                                        <StyledTeamSelectionCard
                                            key={team.teamId}
                                            $isSelected={isSelected}
                                            onClick={() => onToggleTeam(team.teamId)}
                                            data-automation={`scoring-rule-wizard.team-card-${team.teamId}`}
                                        >
                                            <Stack
                                                gap="2px"
                                                flex="1"
                                                minWidth="0"
                                            >
                                                <Text
                                                    fontVariant="subtitle2"
                                                    color={colorsPalettes.carbon["500"]}
                                                >
                                                    {team.name ?? ""}
                                                </Text>
                                                <Text
                                                    fontVariant="caption"
                                                    color={colorsPalettes.carbon["300"]}
                                                >
                                                    {translate("si.management.teams.detail.members_count", {
                                                        count: team.membersCount,
                                                    })}
                                                </Text>
                                            </Stack>
                                            <Checkbox selected={isSelected} />
                                        </StyledTeamSelectionCard>
                                    );
                                })}
                            </StyledTeamSelectionGrid>
                        </Stack>
                    ) : null}
                </Stack>
            </StyledTeamStepScroll>
        </Stack>
    );
};
